import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DEFAULT_QTY_SCALE } from '@shared/forms/forms.constants';
import { isDefined, Maybe } from '@stillmandigital/common';
import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';
import { isEqual, isNil, isNumber, isString } from 'lodash';

export function maxScaleLengthValidator(scale: number = DEFAULT_QTY_SCALE): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isNil(control.value) || control.value === '') {
      return null;
    }
    const precision = BigNumber(control.value).decimalPlaces();

    return isDefined(precision) && precision > scale ? { maxScaleLength: true } : null;
  };
}

export function shouldBeNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isNil(control.value) || control.value === '') {
      return null;
    }

    return isNumber(control.value) ? null : { shouldBeNumber: true };
  };
}

export function notStringValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!isDefined(control.value)) {
      return null;
    }

    return isString(control.value) ? { shouldNotBeString: true } : null;
  };
}

export function oneOfValidator<T>(getValues: () => Maybe<T[]>): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!isDefined(control.value) || control.value === '') {
      return null;
    }

    return getValues()?.some(value => isEqual(value, control.value), control.value) ? null : { oneOf: true };
  };
}

export function integerValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isNil(control.value) || control.value === '') {
      return null;
    }

    return Number.isInteger(Number(control.value)) ? null : { integer: true };
  };
}

export function greaterThanValidator(value: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!isDefined(control.value)) {
      return null;
    }

    return control.value <= value ? { greaterThanValue: true } : null;
  };
}

export function greaterOrEqualToValidator(value: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!isDefined(control.value)) {
      return null;
    }

    return control.value >= value ? null : { greaterThanValue: true };
  };
}

export function assertPropertyValidator<T>(key: keyof T): ValidatorFn {
  return (control: AbstractControl<T>): ValidationErrors | null => {
    if (isNil(control.value) || control.value === '') {
      return null;
    }

    return control.value[key] === undefined ? { assertProperty: true } : null;
  };
}

export function onlyWhitespaceValidator(): ValidatorFn {
  return ({ value }: AbstractControl): ValidationErrors | null =>
    !!value && value.trim().length === 0 ? { onlyWhitespace: true } : null;
}
export function invalidDateRangeValidator(): ValidatorFn {
  return (form: AbstractControl): ValidationErrors | null => {
    const { fromTime, toTime } = form.getRawValue();

    return dayjs(fromTime).isAfter(toTime) ? { invalidDateRange: true } : null;
  };
}
